import { Box, Chip, Divider, Grid, Link, Paper, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet';
import { FileFormat, PayoutReportItem } from '../../gql-types.generated';
import { PayoutReportDisplayName } from '../../util/PayoutReportDisplayName';
import { selectTenantAccount } from '../app/AppSlice';
import { createPayoutReport } from '../home/HomeActions';
import {
  capturePayoutReport,
  capturePayoutReportCancelledInFlight,
  capturePayoutReportError,
  capturePayoutReportInFlight,
  selectPayoutReport,
  selectPayoutReportError,
  selectPayoutReportInFlight,
} from '../home/HomeSlice';
import LoadingMask from '../../components/LoadingMask';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      height: '100%',
      minHeight: 350,
    },
    header: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'start',
      padding: theme.spacing(2.5),
    },
    grid: {
      padding: theme.spacing(3),
    },
    yearFilters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    yearChipSelected: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.uxBlue.activated,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: theme.palette.uxBlue.activated,
      },
    },
    reportList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    reportItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${theme.palette.uxGrey.border}`,
      borderRadius: 10,
      padding: theme.spacing(2, 2.5),
      backgroundColor: theme.palette.background.paper,
    },
    reportInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    reportRange: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.25),
    },
    downloadLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    downloadLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      cursor: 'pointer',
      fontWeight: 500,
    },
    downloadIcon: {
      fontSize: '1.1rem',
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    errorBanner: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 3),
    },
    errorIcon: {
      marginRight: 4,
    },
  }),
);

const Reports: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tenantAccount = useSelector(selectTenantAccount);
  const payoutReport = useSelector(selectPayoutReport);
  const payoutReportError = useSelector(selectPayoutReportError);
  const isFetchingPayoutUrl = useSelector(selectPayoutReportInFlight);

  const payoutReports = tenantAccount?.payoutReports;
  const downloadRef = useRef<HTMLAnchorElement>(null);
  // The payout report id currently being downloaded, so we know which resolved URI to act on.
  const [activeReportId, setActiveReportId] = useState<string | null>(null);

  // Reset the shared payout report state. createPayoutReport leaves the in-flight
  // flag set on success, so it must be cleared here once the download is handled.
  const clearPayoutReportState = () => {
    dispatch(capturePayoutReport(undefined));
    dispatch(capturePayoutReportError(undefined));
    dispatch(capturePayoutReportInFlight(false));
  };

  // Distinct years present in the payout reports, most recent first.
  const years = useMemo(() => {
    const yearSet = new Set<number>();
    (payoutReports || []).forEach(report => {
      yearSet.add(DateTime.fromISO(report.endTime).toUTC().year);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [payoutReports]);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  // Default the selected year to the most recent year with reports.
  useEffect(() => {
    if (years.length > 0 && (selectedYear === undefined || !years.includes(selectedYear))) {
      setSelectedYear(years[0]);
    }
  }, [years]);

  // Reports for the selected year, sorted most recent first.
  const yearReports = useMemo(() => {
    if (selectedYear === undefined) return [];
    return (payoutReports || [])
      .filter(report => DateTime.fromISO(report.endTime).toUTC().year === selectedYear)
      .slice()
      .sort((a, b) => (DateTime.fromISO(a.endTime) > DateTime.fromISO(b.endTime) ? -1 : 1));
  }, [payoutReports, selectedYear]);

  const handleDownload = (payoutReportId?: string | null, format?: FileFormat) => {
    if (payoutReportId && format) {
      setActiveReportId(payoutReportId);
      dispatch(createPayoutReport(payoutReportId, format));
    }
  };

  // Once the access URI for the requested report resolves, trigger the browser download.
  useEffect(() => {
    if (payoutReport?.uri && activeReportId && downloadRef.current) {
      downloadRef.current.href = payoutReport.uri;
      downloadRef.current.click();
      setActiveReportId(null);
      clearPayoutReportState();
    }
  }, [payoutReport, activeReportId, dispatch]);

  // On unmount, cancel any in-flight generation and reset the shared state.
  useEffect(
    () => () => {
      dispatch(capturePayoutReportCancelledInFlight(true));
      clearPayoutReportState();
    },
    [dispatch],
  );

  const getReportRange = (report: PayoutReportItem) => {
    const start = DateTime.fromISO(report.startTime).toUTC();
    const end = DateTime.fromISO(report.endTime).toUTC();
    // e.g. "Apr 1–30" when within the same month, otherwise "Apr 1 – May 2".
    if (start.month === end.month && start.year === end.year) {
      return `${start.toFormat('MMM d')}–${end.toFormat('d')}`;
    }
    return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d')}`;
  };

  return (
    <Paper className={classes.root} role="region" aria-label="Reports">
      <Helmet>
        <meta name="ai:viewId" content="reports"></meta>
        <meta name="ai:viewDescription" content="Aptean Pay Merchant Portal - Reports"></meta>
        <title>Aptean Pay Merchant Portal - Reports</title>
      </Helmet>
      <LoadingMask loading={isFetchingPayoutUrl} />
      <a ref={downloadRef} style={{ display: 'none' }} />
      <Box className={classes.header}>
        <Typography variant="title">Reports</Typography>
      </Box>
      <Divider />
      {payoutReportError && (
        <Box className={classes.errorBanner}>
          <ErrorIcon className={classes.errorIcon} />
          {`${payoutReportError?.message || 'Unable to generate payout report'}. Please try again later!`}
        </Box>
      )}
      <Grid className={classes.grid} container direction="column">
        {years.length > 0 && (
          <Box className={classes.yearFilters} data-cy="report-year-filters">
            {years.map(year => (
              <Chip
                key={year}
                label={year}
                variant="outlined"
                clickable
                onClick={() => setSelectedYear(year)}
                className={selectedYear === year ? classes.yearChipSelected : undefined}
                data-cy={`report-year-${year}`}
              />
            ))}
          </Box>
        )}
        {yearReports.length > 0 ? (
          <Box className={classes.reportList} data-cy="report-list">
            {yearReports.map(report => (
              <Box className={classes.reportItem} key={report.payoutReportId ?? report.endTime} data-cy="report-item">
                <Box className={classes.reportInfo}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {PayoutReportDisplayName(report.startTime, report.endTime)}
                  </Typography>
                  <Typography variant="body2" className={classes.reportRange}>
                    {getReportRange(report)}
                  </Typography>
                </Box>
                <Box className={classes.downloadLinks}>
                  {report.payoutReportId ? (
                    [FileFormat.Csv, FileFormat.Pdf].map(format => (
                      <Link
                        key={format}
                        component="button"
                        type="button"
                        underline="hover"
                        className={classes.downloadLink}
                        disabled={isFetchingPayoutUrl}
                        onClick={() => handleDownload(report.payoutReportId, format)}
                        data-cy={`report-download-${format}`}
                      >
                        <FileDownloadOutlinedIcon className={classes.downloadIcon} />
                        {format === FileFormat.Pdf ? 'PDF' : 'CSV'}
                      </Link>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Unavailable
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box className={classes.emptyState} data-cy="report-empty">
            <Typography variant="body1">No reports available.</Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

export default Reports;
